import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { Button, Center, Column, Row, Screen, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { BackButton } from '~/commonComponents';
import { useToggle } from '~/hooks/useToggle';
import { usePostPermValuePropViewed } from '~/domain/PermanentJobs/useCases/usePostPermValuePropViewed';
import {
  useCreateDraftJobPost,
  useParsePosting,
  useSaveJobPostDetail,
} from '~/domain/PermanentJobs';
import { showErrorMessage } from '~/domain/utils';
import { ParsedPostingMapper } from '~/components/PermanentJobsV2/JobDetailsForm';
import { buildImportConfirmJobDetailsPath } from '~/components/PermanentJobsV2/CreatePosting/Types';
import HowItWorks01 from '~/images/perm/how-it-works-01.svg';
import HowItWorks02 from '~/images/perm/how-it-works-02.svg';
import HowItWorks03 from '~/images/perm/how-it-works-03.svg';

import { firePermValuePropEmailLinkClicked, firePermValuePropGetStarted } from '~/posthogs/events';
import { HowItWorksCard } from './components/HowItWorksCard';
import { CreatePostingMethodPopup } from './components/CreatePostingMethodPopup';
import { OutlineUserGroupIcon } from '@tempmee/react-icons';

const CREATE_POSTING_PATH = '/permanent-jobs/postings/create';

const HOW_IT_WORKS_CARDS = [
  {
    icon: HowItWorks01,
    title: 'Reach Professionals Today',
    description: 'Your role is pushed to licensed dental professionals the moment you post.',
  },
  {
    icon: HowItWorks02,
    title: 'Hassle-Free Scheduling',
    description:
      'Skip the back-and-forth. Candidates request interviews and show up at your office.',
  },
  {
    icon: HowItWorks03,
    title: 'Hire on Your Terms',
    description: 'Select top talent from the largest network of verified dental professionals.',
  },
];

export const ValuePropositionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { mutate: postPermValuePropViewed } = usePostPermValuePropViewed();
  const { mutateAsync: parsePosting } = useParsePosting();
  const { mutateAsync: createDraftJobPost } = useCreateDraftJobPost();
  const { mutateAsync: saveJobPostDetail } = useSaveJobPostDetail();
  const [isMethodPopupOpen, methodPopupControls] = useToggle(false);

  useEffect(() => {
    postPermValuePropViewed();
  }, [postPermValuePropViewed]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get('utm_source') === 'email' && params.get('utm_medium') === 'perm_value_prop') {
      firePermValuePropEmailLinkClicked();
    }
  }, [location.search]);

  const handleStartHiring = () => {
    firePermValuePropGetStarted();
    methodPopupControls.setOn();
  };

  const handleManualAdd = () => {
    methodPopupControls.setOff();
    navigate(CREATE_POSTING_PATH);
  };

  const handleSubmitDescription = async (description: string) => {
    // Copy & Paste import flow: parse the pasted text, then create + save a draft pre-filled
    // with everything that could be extracted, and land on the separate Confirm Job Details
    // page (NOT the manual guided form). On any failure, surface a graceful error and re-throw
    // so the popup keeps the pasted text for a retry and no draft navigation happens.
    let parsed: Awaited<ReturnType<typeof parsePosting>>;
    try {
      parsed = await parsePosting({ text: description });
    } catch (error) {
      // The LLM parse step is where pasted input most often fails (unparseable / insufficient
      // data). Surface a friendly, specific message instead of the raw backend error.
      showErrorMessage({ error: 'Unable to parse job description' });
      throw error;
    }

    try {
      const { id } = await createDraftJobPost();
      await saveJobPostDetail({
        id,
        payload: ParsedPostingMapper.toSaveJobPostDetailPayload(parsed),
      });
      navigate(buildImportConfirmJobDetailsPath(id));
    } catch (error) {
      showErrorMessage({ error });
      throw error;
    }
  };

  return (
    <Screen variant="standard">
      <Screen.Header
        start={
          <span data-testid="value-prop-back-button">
            <BackButton />
          </span>
        }
      />
      <Screen.Body>
        <Center>
          <Column align="center" gap={8} style={{ maxWidth: 800 }}>
            <Column align="center" gap={2}>
              <Typography
                as="span"
                bold
                size="heading_2"
                color={colors.neutral[600]}
                textAlign="center"
              >
                Your Next Permanent Hire is Already on GoTu.
              </Typography>
              <Typography
                as="span"
                variant="body1"
                color={colors.neutral[500]}
                textAlign="center"
              >
                The best dental professionals aren&apos;t on job boards, they&apos;re already
                working.
                <br />
                We put your role directly in front of them.
              </Typography>
            </Column>

            <Column align="center" gap={6} width="100%">
              <Typography
                as="span"
                bold
                size="heading_3"
                color={colors.primary[500]}
                textAlign="center"
              >
                How It Works
              </Typography>
              <Row align="stretch" width="100%">
                {HOW_IT_WORKS_CARDS.map((card, index) => (
                  <React.Fragment key={card.title}>
                    {index > 0 && (
                      <div
                        style={{
                          backgroundColor: colors.neutral[100],
                          width: 1.5,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <HowItWorksCard
                      icon={card.icon}
                      title={card.title}
                      description={card.description}
                    />
                  </React.Fragment>
                ))}
              </Row>
            </Column>

            <Row
              align="center"
              gap={2}
              py={3}
              px={6}
              width="100%"
              className="bg-primary-50 rounded-lg"
            >
              <OutlineUserGroupIcon size={24} color={colors.secondary[500]} />
              <Typography as="span" variant="body1" color={colors.neutral[600]}>
                200,000+ dental professionals on GoTu. Let them know your office is hiring today.
              </Typography>
            </Row>

            <Button
              data-testid="value-prop-start-hiring-button"
              data-cy="value-prop-start-hiring-button"
              isLoading={false}
              size="lg"
              onClick={handleStartHiring}
            >
              Start Hiring
            </Button>
          </Column>
        </Center>

        {isMethodPopupOpen && (
          <CreatePostingMethodPopup
            onClose={methodPopupControls.setOff}
            onManualAdd={handleManualAdd}
            onSubmitDescription={handleSubmitDescription}
          />
        )}
      </Screen.Body>
    </Screen>
  );
};
